import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/apiClient';

interface User {
  id: string;
  email: string;
  name?: string;
  role: string;
  permissions: string[];
  accountType?: string;
  schoolId?: string;
  familyId?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  checkAuth: () => Promise<void>;
  hasRole: (role: string) => boolean;
  hasPermission: (permission: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context) {
    return context;
  }

  const navigate = useNavigate();
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  const setUser = useCallback((user: User | null) => {
    setState(prev => ({
      ...prev,
      user,
      isAuthenticated: !!user,
      error: null,
    }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState(prev => ({
      ...prev,
      error,
    }));
  }, []);

  const setLoading = useCallback((isLoading: boolean) => {
    setState(prev => ({
      ...prev,
      isLoading,
    }));
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');

      if (!token) {
        setUser(null);
        return;
      }

      const response = await apiClient.get<{ data: User }>('/auth/profile');
      setUser(response.data);
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('access_token');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [setUser, setLoading]);

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        setLoading(true);
        setError(null);

        const response = await apiClient.post<{
          user: User;
          accessToken: string;
          refreshToken?: string;
        }>('/auth/login', { email, password });

        // Store the actual JWT token from the backend response
        // The backend returns the JWT in response.accessToken
        localStorage.setItem('access_token', response.accessToken);
        // Also keep sigma08_jwt_token for backward compatibility
        localStorage.setItem('sigma08_jwt_token', response.accessToken);
        if (response.refreshToken) {
          localStorage.setItem('refresh_token', response.refreshToken);
        }

        setUser(response.user);

        const returnUrl = localStorage.getItem('return_url') || '/dashboard';
        localStorage.removeItem('return_url');
        navigate(returnUrl);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Login failed';
        setError(message);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [navigate, setUser, setError, setLoading]
  );

  const logout = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');

      if (token) {
        await apiClient.post('/auth/logout', {}).catch(console.error);
      }

      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      setUser(null);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setLoading(false);
    }
  }, [navigate, setUser, setLoading]);

  const refreshToken = useCallback(async () => {
    try {
      const refresh = localStorage.getItem('refresh_token');

      if (!refresh) {
        throw new Error('No refresh token');
      }

      const response = await apiClient.post<{
        accessToken: string;
        refreshToken?: string;
      }>('/auth/refresh', { refreshToken: refresh });

      // Store the refreshed JWT token
      localStorage.setItem('access_token', response.accessToken);
      localStorage.setItem('sigma08_jwt_token', response.accessToken);
      if (response.refreshToken) {
        localStorage.setItem('refresh_token', response.refreshToken);
      }

      await checkAuth();
    } catch (error) {
      console.error('Token refresh failed:', error);
      await logout();
    }
  }, [checkAuth, logout]);

  const hasRole = useCallback(
    (role: string) => {
      return state.user?.role === role;
    },
    [state.user]
  );

  const hasPermission = useCallback(
    (permission: string) => {
      return state.user?.permissions?.includes(permission) || false;
    },
    [state.user]
  );

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    const interceptor = setInterval(async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiry = payload.exp * 1000;

        if (Date.now() > expiry - 60000) {
          await refreshToken();
        }
      }
    }, 30000);

    return () => clearInterval(interceptor);
  }, [refreshToken]);

  return {
    ...state,
    login,
    logout,
    refreshToken,
    checkAuth,
    hasRole,
    hasPermission,
  };
};

export const AuthProvider = AuthContext.Provider;
export type { User, AuthState, AuthContextType };
